/**
 * Widget State Reducer
 *
 * Manages RoseWidget UI state using useReducer pattern for predictable state transitions.
 * Handles widget display state, loading state, analytics tracking, and hidden zone mode.
 */

import type { ExpandedLayout } from '../../config/constants';

/**
 * Widget state shape - manages UI and tracking state
 * Note: conversationPairs and currentSessionId are managed by useServiceInitialization
 */
export interface WidgetState {
    // Display state
    isExpanded: boolean;
    isExplicitlyMinimized: boolean;
    userClickedHiddenZone: boolean;

    // Loading state
    isLoading: boolean;

    // Analytics tracking (one-time flags)
    hasTrackedImpression: boolean;
    hasTrackedHover: boolean;

    // Error state
    error: string | null;

    // Layout state for expanded view positioning
    viewLayout: ExpandedLayout;
}

export interface WidgetInitialStateOptions {
    viewLayout?: ExpandedLayout;
}

/**
 * Widget action types
 * All state transitions are defined as discrete actions for predictability
 */
export type WidgetAction =
    // State transitions
    | { type: 'EXPAND'; source?: string }
    | { type: 'COLLAPSE' }
    | { type: 'MINIMIZE' }

    // Message flow
    | { type: 'SEND_MESSAGE_START' }
    | { type: 'SEND_MESSAGE_SUCCESS' }
    | { type: 'SEND_MESSAGE_ERROR'; payload: string }

    // Analytics (idempotent)
    | { type: 'TRACK_IMPRESSION' }
    | { type: 'TRACK_HOVER' }

    // Hidden zone mode
    | { type: 'HIDDEN_ZONE_CLICKED' }

    // Layout toggle
    | { type: 'SWITCH_LAYOUT' }
    | { type: 'SET_VIEW_LAYOUT'; payload: ExpandedLayout }

    // Reset
    | { type: 'RESET' };

/**
 * Get initial widget state
 */
export function getInitialState(options: WidgetInitialStateOptions = {}): WidgetState {
    return {
        isExpanded: false,
        isExplicitlyMinimized: false,
        userClickedHiddenZone: false,
        isLoading: false,
        hasTrackedImpression: false,
        hasTrackedHover: false,
        error: null,
        viewLayout: options.viewLayout ?? 'expanded',
    };
}

/**
 * Widget reducer function
 * Pure function that handles all state transitions
 */
export function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
    switch (action.type) {
        case 'EXPAND':
            return {
                ...state,
                isExpanded: true,
            };

        case 'COLLAPSE':
            return {
                ...state,
                isExpanded: false,
                isExplicitlyMinimized: false,
            };

        case 'MINIMIZE':
            return {
                ...state,
                isExpanded: false,
                isExplicitlyMinimized: true,
            };

        case 'SEND_MESSAGE_START':
            return {
                ...state,
                isLoading: true,
                error: null,
            };

        case 'SEND_MESSAGE_SUCCESS':
            return {
                ...state,
                isLoading: false,
                isExpanded: true, // Auto-expand on successful message
            };

        case 'SEND_MESSAGE_ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };

        case 'TRACK_IMPRESSION':
            // Idempotent - only track once
            if (state.hasTrackedImpression) return state;
            return {
                ...state,
                hasTrackedImpression: true,
            };

        case 'TRACK_HOVER':
            // Idempotent - only track once
            if (state.hasTrackedHover) return state;
            return {
                ...state,
                hasTrackedHover: true,
            };

        case 'HIDDEN_ZONE_CLICKED':
            return {
                ...state,
                userClickedHiddenZone: true,
                isExpanded: true, // Auto-expand when hidden zone is clicked
            };

        case 'SWITCH_LAYOUT':
            return {
                ...state,
                viewLayout: state.viewLayout === 'expanded' ? 'copilot' : 'expanded',
            };

        case 'SET_VIEW_LAYOUT':
            if (state.viewLayout === action.payload) {
                return state;
            }
            return {
                ...state,
                viewLayout: action.payload,
            };

        case 'RESET':
            return getInitialState();

        default:
            return state;
    }
}
